import { CRM_PRO_ROUTES } from "../../pages/crm-pro/constants";

export const SEVERITY_VALUES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;
export type Severity = (typeof SEVERITY_VALUES)[number];

export interface NotificationItem {
  id: number;
  title: string;
  body: string;
  type: string;
  severity: Severity;
  data?: Record<string, any>;
  isRead: boolean;
  createdAt?: string;
}

export interface NotificationListResult {
  rows: NotificationItem[];
  count: number;
  page: number;
  limit: number;
  unreadCount: number;
}

const getBooleanReadState = (raw: any): boolean => {
  return Boolean(raw?.isRead ?? raw?.is_read ?? raw?.read ?? raw?.is_reading ?? false);
};

export const normalizeNotification = (raw: any): NotificationItem => {
  const severity = String(raw?.severity ?? "LOW").toUpperCase();
  return {
    id: Number(raw?.id ?? 0),
    title: String(raw?.title ?? ""),
    body: String(raw?.body ?? ""),
    type: String(raw?.type ?? "GENERAL"),
    severity: SEVERITY_VALUES.includes(severity as Severity) ? (severity as Severity) : "LOW",
    data: raw?.data,
    isRead: getBooleanReadState(raw),
    createdAt: raw?.createdAt ?? raw?.created_at ?? raw?.sentAt ?? raw?.sent_at,
  };
};

export const normalizeNotificationListResponse = (payload: any): NotificationListResult => {
  const rowsSource = Array.isArray(payload?.rows)
    ? payload.rows
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.notifications)
        ? payload.notifications
        : [];

  const rows = rowsSource.map(normalizeNotification);
  const unreadCount = Number(payload?.unreadCount ?? payload?.unread_count ?? rows.filter((item) => !item.isRead).length);

  return {
    rows,
    count: Number(payload?.count ?? rows.length),
    page: Number(payload?.page ?? 1),
    limit: Number(payload?.limit ?? rows.length),
    unreadCount,
  };
};

const SEVERITY_BORDER_COLORS: Record<Severity, string> = {
  LOW: "#22c55e",
  MEDIUM: "#eab308",
  HIGH: "#f97316",
  CRITICAL: "#ef4444",
};

export const getSeverityBorder = (severity: string): string => {
  const key = String(severity).toUpperCase() as Severity;
  return SEVERITY_BORDER_COLORS[key] || SEVERITY_BORDER_COLORS.LOW;
};

export const resolveNotificationPath = (data?: Record<string, any>): string | null => {
  if (!data) return null;

  const directPath = data.path || data.route || data.url || data.redirectTo;
  if (typeof directPath === "string" && directPath) {
    if (directPath.startsWith("#/")) return directPath.slice(1);
    if (directPath.startsWith("/")) return directPath;
  }

  const projectId = data.projectId ?? data.project_id;
  if (projectId) return `/projects/${projectId}`;

  const leadId = data.leadId ?? data.lead_id;
  if (leadId) return `${CRM_PRO_ROUTES.leads}/${leadId}`;

  return null;
};
